import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import SourcesTable from './SourcesTable';
import PageHeader from 'Utils/PageHeader';
import { getFetchHandler } from 'Secure/Utils';
import {
  getLatestDataSourceInventory,
  getSensitiveFieldCountFromInventory,
  inventoryContentOf,
  listMaskingJobs,
  mutatedDataSourceIdsOf,
  readableJobError,
} from 'Secure/api';
import FetchContainer from 'Utils/FetchContainer';
import MaskContext from './MaskContext';
import { usePolling } from 'Hooks';
import { FaMask } from 'react-icons/fa';
import AppContext from 'Utils/AppContext';
import { getDataSourceNumericId, filterJobStartable } from 'DataSources/datasourcesApi';
import { compareByLatestJobDesc, jobTimestampMs } from 'Secure/jobSortOrder';
import { getHistory, getResultsPayloadForExport } from 'DataDiscovery/discoverApi';
import { listDrivers } from 'Drivers/api';
import { hasMaskingDriverForEngine } from 'Drivers/maskingEngineMatch';
import { supportsFileMaskingForConnection } from 'Drivers/fileFormatSupport';
import { getServiceCapabilities, supportedFileFormatsForRole } from 'Secure/serviceCapabilitiesApi';


function MaskingSources() {
  const [data, setData] = useState({});
  const [error, setError] = useState(null);
  const [fetchingData, setFetchingData] = useState(false);
  const [maskOptions, setMaskOptions] = useState({});
  const { connections } = useContext(AppContext);
  const fetchData = useMemo(() => {
    try {
      const response =  getFetchHandler(setFetchingData, setError, setData, getMaskingSources);

      return (response ? response : []);
    } catch (e) {
      console.log('Error fetching data', e);
      return [];
    }
  }, []);
  
  const fetchDataHandler = useCallback(async (from, sortKey, searchTerms) => {
    await fetchData(connections, from, sortKey, searchTerms);
  }, [fetchData, connections]);

  const maskContext = { maskOptions, setMaskOptions, fetchDataHandler };

  useEffect(() => {
    const init = async () => {
      await fetchDataHandler(0);
    }
    init();
  }, [fetchDataHandler]);

  const maskPollDelayMs = useMemo(() => {
    const sources = data.sources || [];
    const anyRunning = sources.some((s) => s && s.status === 'RUNNING');
    return anyRunning ? 5000 : 30000;
  }, [data.sources]);

  usePolling(
    async () => {
      if (!fetchingData) {
        await fetchDataHandler();
      }
    },
    maskPollDelayMs
  );

  return (
    <div>
      <FetchContainer error={error} fluid>
        <div>
          <PageHeader
            header='Masking'
            icon={FaMask}
            tooltip='Mask discovered data sources using format preserving masking. Click to the gear icons to view or manage fields enabled for masking.'
          />
          <div>
            <MaskContext.Provider value={maskContext}>
              <SourcesTable
                data={data}
                fetchingData={fetchingData}
                fetchDataHandler={fetchDataHandler}
                maskOptions={maskOptions}
              />
            </MaskContext.Provider>
          </div>
        </div>
      </FetchContainer>
    </div>
  );
}

async function getMaskingSources(connections, _from, _sortKey, searchTerms) {
  // Imported-but-not-adopted rows (registeredServiceId present, managedBy !== 'c2')
  // don't have credentials — we can't mask them. Exclude them up front.
  // mongodb is included: mongo masking is supported (mongodb→mongodb). dynamodb is NOT —
  // no NoSQL masking for dynamo yet; platform rejects with ENGINE_NOT_MASKABLE. Legacy
  // s3/sftp `c.type` rows are let through so file sources surface and have their
  // capability checked per-format below.
  const filteredConnections = filterJobStartable(connections)
    .filter((c) => (c.type || '').toLowerCase() !== 'dynamodb')
    .filter((c) => (searchTerms ? String(c.name || '').toLowerCase().includes(String(searchTerms).toLowerCase()) : true));

  const [jobsResponse, drivers, capabilities] = await Promise.all([
    listMaskingJobs({ page: 0, size: 500, sort: ['id,desc'] }),
    listDrivers().catch(() => []),
    getServiceCapabilities(),
  ]);
  const maskingFileFormats = supportedFileFormatsForRole(capabilities, 'masking');
  const jobs = jobsResponse && Array.isArray(jobsResponse.content) ? jobsResponse.content : [];
  // Keyed on the MUTATED sources: the cross-source targets when the job has any (a fan-out
  // job mutates several), else the in-place source. A cross-source job masks its targets —
  // the source of record it read from must stay UNSECURED/UNMASKED unless some other job
  // masked it in place.
  const jobsByDataSource = {};
  jobs.forEach((job) => {
    if (!job) return;
    mutatedDataSourceIdsOf(job).forEach((mutatedId) => {
      if (mutatedId != null && !jobsByDataSource[mutatedId]) {
        jobsByDataSource[mutatedId] = job;
      }
    });
  });

  const enrichedSources = await Promise.all(
    filteredConnections.map(async (conn) => {
      const source = {
        connection_name: conn.name,
        dataSourceId: conn.id ?? null,
        engine: '',
        status: 'UNSECURED',
        time_stamp: null,
        prevSearchid: null,
        prevSearchName: '—',
        fieldQty: 0,
        errorMessage: null,
        readOnly: conn.readOnly === true,
        tableData: conn.id != null ? { id: conn.id } : undefined,
      };
      const dataSourceId = getDataSourceNumericId(source);
      const meta =
        dataSourceId != null ? await getDiscoveryRowMetadata(dataSourceId) : { latestRun: null, discoverFieldCount: 0 };

      // A source can have several named inventories; the row summarizes the most recently
      // updated one (field count + PROVIDER column). The inventory page is where the user
      // picks a specific inventory.
      let fieldQty = 0;
      let latestInventory = null;
      try {
        if (dataSourceId != null) {
          latestInventory = await getLatestDataSourceInventory(dataSourceId);
          fieldQty = getSensitiveFieldCountFromInventory(inventoryContentOf(latestInventory));
          if (!fieldQty) fieldQty = meta.discoverFieldCount;
        }
      } catch (e) {
        if (dataSourceId != null) fieldQty = meta.discoverFieldCount;
      }

      const job = dataSourceId != null ? jobsByDataSource[dataSourceId] : null;
      const status = mapMaskingJobStatus(job && job.status);
      const engine = displayEngineForMaskingRow(conn, meta.latestRun);
      const fileSupport = supportsFileMaskingForConnection(conn, maskingFileFormats);
      const hasMaskingDriver = fileSupport === null
        ? hasMaskingDriverForEngine(engine, drivers)
        : fileSupport;
      return {
        ...source,
        engine,
        datasourceType: conn.datasourceType || null,
        preferredMaskingProviderId: latestInventory?.preferredMaskingProviderId || null,
        prevSearchid: meta.latestRun ? meta.latestRun.run_id : null,
        prevSearchName: formatDiscoveryLinkLabel(meta.latestRun),
        fieldQty,
        status,
        // Human-readable summary (code + parsed message), never the raw upstream JSON blob.
        errorMessage: job && status === 'ERROR' ? readableJobError(job) : null,
        maskingJobProgress: maskingJobProgressFromJob(job),
        latestJobId: job ? job.id : null,
        latestJobAt: jobTimestampMs(job),
        hasMaskingDriver,
      };
    })
  );

  // Most recently run job first, sources with no job last — a just-started job stays on page 1.
  const sources = enrichedSources.filter((s) => s.fieldQty > 0).sort(compareByLatestJobDesc);

  return {
    sources,
    totalConnections: sources.length,
  };
}

/**
 * One history fetch per row: latest job (for link + engine) + field count from latest completed run.
 */
async function getDiscoveryRowMetadata(dataSourceId) {
  try {
    const history = await getHistory(0, 50, 'startedAt,desc', dataSourceId);
    const runs = history && history.runs ? history.runs : [];
    const latestRun = runs[0] || null;
    const latestSuccessful = runs.find((run) => run && run.status === 'COMPLETED');
    let discoverFieldCount = 0;
    if (latestSuccessful && latestSuccessful.run_id) {
      // Folder Search runs persist only the folder INDEX (no tables) —
      // getResultsPayloadForExport unions the per-folder chunks so generic
      // sources count their real sensitive fields instead of zero (which the
      // fieldQty > 0 row filter would otherwise drop from the page entirely).
      const payload = (await getResultsPayloadForExport(latestSuccessful.run_id)) || {};
      const tables = Array.isArray(payload.tables) ? payload.tables : [];
      tables.forEach((table) => {
        (table.fields || []).forEach((field) => {
          const findings = field && field.findings ? field.findings : {};
          if (Object.keys(findings).length > 0) {
            discoverFieldCount += 1;
          }
        });
      });
    }
    return { latestRun, discoverFieldCount };
  } catch (e) {
    return { latestRun: null, discoverFieldCount: 0 };
  }
}

function displayEngineForMaskingRow(conn, latestRun) {
  const t = (conn && conn.type ? String(conn.type) : '').trim();
  const lower = t.toLowerCase();
  if (lower && lower !== 'jdbc') return lower;
  const jobEngine = latestRun && latestRun.engine && String(latestRun.engine).trim();
  if (jobEngine) return jobEngine.toLowerCase();
  return lower;
}

function formatDiscoveryLinkLabel(latestRun) {
  if (!latestRun) return '—';
  const label = latestRun.label != null ? String(latestRun.label).trim() : '';
  if (label) return label;
  return latestRun.run_id ? String(latestRun.run_id) : '—';
}

function parseMaskingJobCachedJson(raw) {
  if (raw == null || raw === '') return {};
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch {
    return {};
  }
}

function firstNonNegativeNumber(...vals) {
  for (const v of vals) {
    if (v == null || v === '') continue;
    const n = Number(v);
    if (!Number.isNaN(n) && n >= 0) return n;
  }
  return null;
}

function firstTimestampMs(...vals) {
  for (const v of vals) {
    if (v == null || v === '') continue;
    const ms = typeof v === 'number' ? v : Date.parse(v);
    if (!Number.isNaN(ms) && ms > 0) return ms;
  }
  return null;
}

/**
 * Progress fields from GET /api/v1/masking/jobs (same shape as discover orchestration jobs where applicable).
 */
function maskingJobProgressFromJob(job) {
  if (!job) return null;
  const cached = parseMaskingJobCachedJson(job.cachedStatusJson);
  const tablesTotal = firstNonNegativeNumber(
    job.workItemsTotal,
    cached.workItemsTotal,
    job.totalTables,
    cached.totalTables
  );
  const tablesCompleted = firstNonNegativeNumber(
    job.workItemsCompleted,
    cached.workItemsCompleted,
    job.tablesCompleted,
    cached.tablesCompleted,
    job.completedTables,
    cached.completedTables
  );
  const recordsTotal = firstNonNegativeNumber(
    job.recordsTotal,
    cached.recordsTotal,
    cached.total_rows_to_process,
    cached.totalRowsToProcess,
    job.tableRecordsTotal,
    cached.tableRecordsTotal
  );
  const recordsCompleted = firstNonNegativeNumber(
    job.recordsProcessed,
    cached.recordsProcessed,
    cached.rowsProcessed,
    job.recordsCompleted,
    cached.recordsCompleted,
    job.tableRecordsProcessed,
    cached.tableRecordsProcessed
  );
  const currentTableName =
    job.currentTableName ||
    job.currentTable ||
    cached.currentTableName ||
    cached.currentTable ||
    null;
  const startedAtMs = firstTimestampMs(
    job.startedAt,
    cached.startedAt,
    cached.creationTime,
    job.submittedAt
  );
  const hasTableProgress = tablesTotal != null && tablesTotal > 0;
  if (
    !hasTableProgress &&
    recordsTotal == null &&
    recordsCompleted == null &&
    !currentTableName &&
    startedAtMs == null
  ) {
    return null;
  }
  return {
    tablesCompleted: hasTableProgress
      ? Math.min(tablesCompleted ?? 0, tablesTotal)
      : tablesCompleted,
    tablesTotal: hasTableProgress ? tablesTotal : null,
    recordsCompleted,
    recordsTotal,
    startedAtMs,
    currentTableName:
      currentTableName != null && String(currentTableName).trim() !== ''
        ? String(currentTableName).trim()
        : null,
  };
}

function mapMaskingJobStatus(status) {
  switch (status) {
    case 'PENDING':
    case 'RUNNING':
      return 'RUNNING';
    case 'SUCCEEDED':
      return 'MASKED';
    case 'FAILED':
      return 'ERROR';
    case 'CANCELLED':
      return 'UNMASKED';
    default:
      return 'UNSECURED';
  }
}

export default MaskingSources;